import React, { useState, useRef, useEffect } from "react";
import { useDispatch } from "react-redux";
import ChangePasswordModal from "./../Modules/LogIn/ChangePasswordPage";
import { fetchHolidays } from "../API_Service/Settings_Slice";
import { companyID } from "../Common/UIComponents";
import { useNotifications } from "../Common/useNotifications";
import NotificationPanel    from "../Common/NotificationPanel";
import Toast from "../Common/Toastify";

// const roleName = localStorage.getItem("roleName") || "";

// ─────────────────────────────────────────────
// HOLIDAY CALENDAR MODAL
// ─────────────────────────────────────────────

const MONTH_NAMES = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];
const DAY_NAMES = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];


const parseDate = (str) => {
  if (!str) return null;
  const d = new Date(str);
  return isNaN(d.getTime()) ? null : d;
};

const toDateKey = (y, m, d) => `${y}-${m}-${d}`;

const HolidayCalendarModal = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [current, setCurrent]   = useState(() => {
    const n = new Date();
    return { year: n.getFullYear(), month: n.getMonth() };
  });
  const [tooltip, setTooltip] = useState(null); // { x, y, holidays }

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    dispatch(fetchHolidays({ ID: 0, companyID }))
      .then((res) => {
        const data = res?.payload?.data ?? res?.payload ?? [];
        setHolidays(Array.isArray(data) ? data : []);
      })
      .finally(() => setLoading(false));
  }, [isOpen, dispatch]);

  if (!isOpen) return null;

  const { year, month } = current;
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Type → color map
  const typeColorMap = {};
  const PILL_COLORS = [
    "bg-rose-100 text-rose-700",
    "bg-amber-100 text-amber-700",
    "bg-violet-100 text-violet-700",
    "bg-sky-100 text-sky-700",
    "bg-orange-100 text-orange-700",
  ];
  holidays.forEach((h) => {
    if (h.HolidayTypeName && !typeColorMap[h.HolidayTypeName]) {
      typeColorMap[h.HolidayTypeName] =
        PILL_COLORS[Object.keys(typeColorMap).length % PILL_COLORS.length];
    }
  });

  // Date-key → holidays map
  const holidayMap = {};
  holidays.forEach((h) => {
    const from = parseDate(h.HolidayFromDate);
    const to   = parseDate(h.HolidayToDate) ?? from;
    if (!from) return;
    const pill = typeColorMap[h.HolidayTypeName] ?? PILL_COLORS[0];
    const cur = new Date(from);
    while (cur <= to) {
      const key = toDateKey(cur.getFullYear(), cur.getMonth(), cur.getDate());
      if (!holidayMap[key]) holidayMap[key] = [];
      holidayMap[key].push({ name: h.HolidayName, typeName: h.HolidayTypeName, pill });
      cur.setDate(cur.getDate() + 1);
    }
  });

  // Calendar grid
  const firstDay    = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const prevMonth = () =>
    setCurrent((p) => p.month === 0 ? { year: p.year - 1, month: 11 } : { ...p, month: p.month - 1 });
  const nextMonth = () =>
    setCurrent((p) => p.month === 11 ? { year: p.year + 1, month: 0 } : { ...p, month: p.month + 1 });
  const goToday   = () => setCurrent({ year: today.getFullYear(), month: today.getMonth() });

  // Holidays in current viewed month
  const monthHolidayCount = Object.keys(holidayMap).filter((k) => {
    const [y, m] = k.split("-").map(Number);
    return y === year && m === month;
  }).length;

  // All future holidays grouped by month
  const upcoming = holidays
    .filter((h) => {
      const to = parseDate(h.HolidayToDate ?? h.HolidayFromDate);
      return to && to >= today;
    })
    .sort((a, b) => new Date(a.HolidayFromDate) - new Date(b.HolidayFromDate));

  // Group upcoming by "Month Year"
  const grouped = {};
  upcoming.forEach((h) => {
    const d = parseDate(h.HolidayFromDate);
    if (!d) return;
    const key = `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`;
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(h);
  });

  const daysUntil = (str) => {
    const d = parseDate(str);
    if (!d) return null;
    d.setHours(0, 0, 0, 0);
    const diff = Math.ceil((d - today) / 86400000);
    if (diff === 0) return "Today";
    if (diff === 1) return "Tomorrow";
    if (diff <= 7)  return `In ${diff} days`;
    return null;
  };

  const fmtDate = (str, opts) => parseDate(str)?.toLocaleDateString("en-GB", opts) ?? "";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      {/* Fixed tooltip — escapes any overflow container */}
      {tooltip && (
        <div
          className="fixed z-[9999] w-48 bg-[#181c1d] text-white rounded-xl px-3 py-2.5 shadow-2xl pointer-events-none"
          style={{ left: tooltip.x, top: tooltip.y - 8, transform: "translate(-50%, -100%)" }}
        >
          <div className="space-y-1.5">
            {tooltip.holidays.map((h, i) => (
              <div key={i} className="min-w-0">
                <p className="text-[11px] font-bold leading-tight break-words">{h.name}</p>
                {h.typeName && (
                  <span className={`inline-block mt-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded-full break-words max-w-full ${h.pill}`}>
                    {h.typeName}
                  </span>
                )}
              </div>
            ))}
          </div>
          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-[#181c1d]" />
        </div>
      )}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

      <div
        className="relative bg-[#f7fafa] rounded-3xl shadow-2xl w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Gradient Header ── */}
        <div className="bg-gradient-to-br from-[#005b60] to-[#08757c] px-7 py-5 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-11 h-11 rounded-2xl bg-white/15 flex items-center justify-center">
                <span className="material-symbols-outlined text-[24px] text-white">calendar_month</span>
              </div>
              <div>
                <h2 className="text-lg font-extrabold text-white tracking-tight">Holiday Calendar</h2>
                <p className="text-[11px] text-white/60 mt-0.5">
                  {holidays.length} holidays · {upcoming.length} upcoming
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            >
              <span className="material-symbols-outlined text-[20px] text-white">close</span>
            </button>
          </div>
        </div>

        {/* ── Body ── */}
        <div className="flex flex-1 overflow-hidden">

          {/* ── Left: Calendar ── */}
          <div className="flex-1 p-6 overflow-y-auto border-r border-[#e5e9e9]">

            {/* Month nav */}
            <div className="flex items-center justify-between mb-5">
              <button
                onClick={prevMonth}
                className="w-9 h-9 rounded-xl bg-white hover:bg-[#e5e9e9] border border-[#e5e9e9] flex items-center justify-center transition-colors shadow-sm"
              >
                <span className="material-symbols-outlined text-[20px] text-[#3e494a]">chevron_left</span>
              </button>

              <div className="text-center">
                <h3 className="text-xl font-extrabold text-[#181c1d]">{MONTH_NAMES[month]} {year}</h3>
                {monthHolidayCount > 0 ? (
                  <span className="text-[10px] font-bold text-[#08757c] bg-[#08757c]/10 px-2 py-0.5 rounded-full">
                    {monthHolidayCount} holiday{monthHolidayCount > 1 ? "s" : ""} this month
                  </span>
                ) : (
                  !loading && (
                    <span className="text-[10px] font-bold text-[#6e797a] bg-[#e5e9e9] px-2 py-0.5 rounded-full">
                      No holidays for this month
                    </span>
                  )
                )}
              </div>

              <button
                onClick={nextMonth}
                className="w-9 h-9 rounded-xl bg-white hover:bg-[#e5e9e9] border border-[#e5e9e9] flex items-center justify-center transition-colors shadow-sm"
              >
                <span className="material-symbols-outlined text-[20px] text-[#3e494a]">chevron_right</span>
              </button>
            </div>

            {/* Today button */}
            <div className="flex justify-center mb-4">
              <button
                onClick={goToday}
                className="text-[11px] font-bold text-[#08757c] hover:underline flex items-center gap-1"
              >
                <span className="material-symbols-outlined text-[14px]">my_location</span>
                Go to Today
              </button>
            </div>

            {/* Day headers */}
            <div className="grid grid-cols-7 mb-2">
              {DAY_NAMES.map((d, i) => (
                <div
                  key={d}
                  className={`text-center text-[11px] font-extrabold uppercase tracking-widest py-2 ${
                    i === 0 || i === 6 ? "text-rose-400" : "text-[#3e494a]/40"
                  }`}
                >
                  {d}
                </div>
              ))}
            </div>

            {/* Grid */}
            {loading ? (
              <div className="flex flex-col items-center justify-center h-52 gap-3">
                <span className="material-symbols-outlined text-[40px] text-[#005b60] animate-spin">progress_activity</span>
                <p className="text-sm text-[#3e494a]">Loading holidays…</p>
              </div>
            ) : (
              <div className="grid grid-cols-7 gap-1.5">
                {cells.map((day, idx) => {
                  if (!day) return <div key={idx} className="min-h-[64px]" />;

                  const key      = toDateKey(year, month, day);
                  const dayHols  = holidayMap[key] ?? [];
                  const cellDate = new Date(year, month, day);
                  cellDate.setHours(0, 0, 0, 0);
                  const isToday   = cellDate.getTime() === today.getTime();
                  const isPast    = cellDate < today;
                  const isHoliday = dayHols.length > 0;
                  const dow       = cellDate.getDay();
                  const isWeekend = dow === 0 || dow === 6;
                  return (
                    <div
                      key={idx}
                      className={`relative flex flex-col rounded-2xl min-h-[64px] transition-all duration-150 cursor-default overflow-visible
                        ${isToday
                          ? "bg-gradient-to-br from-[#005b60] to-[#08757c] shadow-lg shadow-[#005b60]/30"
                          : isHoliday
                          ? "bg-white border-2 border-rose-200 shadow-sm"
                          : isPast
                          ? "bg-[#e5e9e9] border border-[#d3d9d9]"
                          : "bg-white border border-[#e5e9e9] hover:border-[#08757c]/40 hover:shadow-sm"
                        }`}
                      onMouseEnter={(e) => {
                        if (!isHoliday) return;
                        const r = e.currentTarget.getBoundingClientRect();
                        setTooltip({ x: r.left + r.width / 2, y: r.top, holidays: dayHols });
                      }}
                      onMouseLeave={() => setTooltip(null)}
                    >
                      {/* Day number */}
                      <div className="flex items-center justify-between px-2 pt-2">
                        <span className={`text-xs font-extrabold leading-none ${
                          isToday   ? "text-white" :
                          isHoliday ? "text-rose-600" :
                          isPast    ? "text-[#6e797a]" :
                          isWeekend ? "text-rose-400" :
                          "text-[#181c1d]"
                        }`}>
                          {day}
                        </span>
                        {isHoliday && !isToday && (
                          <span className="w-1.5 h-1.5 rounded-full bg-rose-400 flex-shrink-0" />
                        )}
                        {isToday && (
                          <span className="text-[8px] font-black text-white/70 uppercase tracking-widest">Today</span>
                        )}
                      </div>

                      {/* Holiday label */}
                      {isHoliday && (
                        <div className="px-1.5 pb-1.5 mt-1 flex-1">
                          <p className={`text-[8px] font-bold leading-tight truncate ${isToday ? "text-white/80" : "text-rose-600"}`}>
                            {dayHols[0].name}
                          </p>
                          {dayHols.length > 1 && (
                            <p className="text-[7px] text-[#3e494a]/50 font-bold">+{dayHols.length - 1} more</p>
                          )}
                        </div>
                      )}

                    </div>
                  );
                })}
              </div>
            )}

            {/* Legend */}
            <div className="flex items-center gap-5 mt-5 pt-4 border-t border-[#e5e9e9]">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-lg bg-gradient-to-br from-[#005b60] to-[#08757c]" />
                <span className="text-[11px] text-[#3e494a] font-medium">Today</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-lg border-2 border-rose-200 bg-white" />
                <span className="text-[11px] text-[#3e494a] font-medium">Holiday</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-lg bg-[#e5e9e9] border border-[#d3d9d9]" />
                <span className="text-[11px] text-[#3e494a] font-medium">Past date</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-extrabold text-rose-400">S</span>
                <span className="text-[11px] text-[#3e494a] font-medium">Weekend</span>
              </div>
            </div>
          </div>

          {/* ── Right: Upcoming ── */}
          <div className="w-72 flex flex-col flex-shrink-0">
            <div className="px-5 py-4 border-b border-[#e5e9e9] flex-shrink-0">
              <div className="flex items-center justify-between">
                <h4 className="text-xs font-extrabold uppercase tracking-widest text-[#3e494a]">
                  Upcoming Holidays
                </h4>
                {upcoming.length > 0 && (
                  <span className="text-[10px] font-bold bg-[#005b60] text-white px-2 py-0.5 rounded-full">
                    {upcoming.length}
                  </span>
                )}
              </div>
            </div>

            <div className="overflow-y-auto flex-1 px-4 py-3">
              {loading ? (
                <div className="space-y-3">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="h-16 bg-[#e5e9e9] rounded-2xl animate-pulse" />
                  ))}
                </div>
              ) : upcoming.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-40 gap-2">
                  <span className="material-symbols-outlined text-[36px] text-[#3e494a]/20">event_busy</span>
                  <p className="text-xs text-[#3e494a]/40 font-medium">No upcoming holidays</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {Object.entries(grouped).map(([monthLabel, items]) => (
                    <div key={monthLabel}>
                      {/* Month divider */}
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-[10px] font-extrabold uppercase tracking-widest text-[#08757c]">
                          {monthLabel}
                        </span>
                        <div className="flex-1 h-px bg-[#08757c]/20" />
                      </div>

                      <div className="space-y-2">
                        {items.map((h) => {
                          const from = parseDate(h.HolidayFromDate);
                          const to   = parseDate(h.HolidayToDate);
                          const sameDay = !to || from?.toDateString() === to?.toDateString();
                          const pill    = typeColorMap[h.HolidayTypeName] ?? PILL_COLORS[0];
                          const badge   = daysUntil(h.HolidayFromDate);

                          return (
                            <div
                              key={h.HolidayID}
                              className="bg-white rounded-2xl p-3 border border-[#e5e9e9] shadow-sm flex gap-3"
                            >
                              {/* Date box */}
                              <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-[#005b60]/8 bg-[#f1f4f4] flex flex-col items-center justify-center">
                                <span className="text-[16px] font-extrabold text-[#005b60] leading-none">
                                  {from?.getDate()}
                                </span>
                                <span className="text-[8px] font-bold text-[#3e494a]/50 uppercase">
                                  {from ? MONTH_NAMES[from.getMonth()].slice(0, 3) : ""}
                                </span>
                              </div>

                              <div className="min-w-0 flex-1">
                                <div className="flex items-start justify-between gap-1">
                                  <p className="text-[11px] font-bold text-[#181c1d] leading-tight break-words min-w-0">{h.HolidayName}</p>
                                  {badge && (
                                    <span className={`flex-shrink-0 text-[8px] font-extrabold px-1.5 py-0.5 rounded-full whitespace-nowrap ${
                                      badge === "Today"    ? "bg-[#005b60] text-white" :
                                      badge === "Tomorrow" ? "bg-amber-100 text-amber-700" :
                                      "bg-[#f1f4f4] text-[#3e494a]"
                                    }`}>
                                      {badge}
                                    </span>
                                  )}
                                </div>
                                {!sameDay && (
                                  <p className="text-[9px] text-[#3e494a]/50 mt-0.5">
                                    {fmtDate(h.HolidayFromDate, { day: "numeric", month: "short" })} –{" "}
                                    {fmtDate(h.HolidayToDate,   { day: "numeric", month: "short" })}
                                  </p>
                                )}
                                {h.HolidayTypeName && (
                                  <span className={`inline-block mt-1 text-[8px] font-bold px-1.5 py-0.5 rounded-full break-words max-w-full ${pill}`}>
                                    {h.HolidayTypeName}
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// TOP NAV
// ─────────────────────────────────────────────

const TopNav = ({ onGlobalSearch, onLogout, user }) => {
  const [searchValue,         setSearchValue]         = useState("");
  const [profileOpen,         setProfileOpen]         = useState(false);
  const [changePasswordOpen,  setChangePasswordOpen]  = useState(false);
  const [calendarOpen,        setCalendarOpen]        = useState(false);
  const [currentEmployeeName, setCurrentEmployeeName] = useState(localStorage.getItem("empName") || "");
  const [currentRoleName,     setCurrentRoleName]     = useState(localStorage.getItem("roleName") || "");

  // Listen for storage changes (when user logs in/out on same tab)
  useEffect(() => {
    const handleStorageChange = () => {
      const newEmpName = localStorage.getItem("empName") || "";
      const newRoleName = localStorage.getItem("roleName") || "";
      setCurrentEmployeeName(newEmpName);
      setCurrentRoleName(newRoleName);
    };

    // Listen for custom roleChanged event (from sidebar role switch)
    const handleRoleChanged = (event) => {
      const role = event.detail;
      if (role && role.RoleName) {
        setCurrentRoleName(role.RoleName);
      }
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("roleChanged", handleRoleChanged);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("roleChanged", handleRoleChanged);
    };
  }, []);

  const handleAccountDeactivated = () => {
  Toast?.("Your account has been deactivated by an administrator.", "error");
  onLogout?.(); 
};

  const {
  notifications,
  unreadCount,
  connected,
  markRead,
  markAllRead,
  clearAll,
} = useNotifications(handleAccountDeactivated);
  const dropdownRef = useRef(null);

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchValue(value);
    if (onGlobalSearch) onGlobalSearch(value);
  };

  const handleLogout = () => {
    setProfileOpen(false);
    if (onLogout) onLogout();
  };

  const handleChangePassword = () => {
    setProfileOpen(false);
    setChangePasswordOpen(true);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <>
      <header className="sticky top-0 z-40 w-full bg-white/95 backdrop-blur-sm border-b border-slate-100 shadow-[0_2px_16px_rgba(0,0,0,0.06)]">
        <div className="flex items-center justify-between px-6 h-16 gap-4">

          <div className="flex items-center flex-1 max-w-sm">
            <div className="relative w-full">
              {/* <span className="absolute inset-y-0 left-3.5 flex items-center pointer-events-none">
                <span className="material-symbols-outlined text-[18px] text-slate-400">search</span>
              </span>
              <input
                value={searchValue}
                onChange={handleSearch}
                placeholder="Search anything..."
                className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#08757c]/25 focus:border-[#08757c] transition-all"
                type="text"
              /> */}
            </div>
          </div>
         

          {/* ── Right actions ── */}
          <div className="flex items-center gap-1">

            {/* Icon buttons */}
            {[
              { icon: "calendar_today",  title: "Holiday Calendar", onClick: () => setCalendarOpen(true) },
              // { icon: "notifications",   title: "Notifications",    onClick: () => {} },
              // { icon: "help",            title: "Help",             onClick: () => {} },
              // { icon: "settings",        title: "Settings",         onClick: () => {} },
            ].map(({ icon, title, onClick }) => (
              <button
                key={icon}
                onClick={onClick}
                title={title}
                className="w-9 h-9 flex items-center justify-center rounded-xl text-slate-500 hover:text-[#08757c] hover:bg-[#08757c]/8 transition-all duration-150"
              >
                <span className="material-symbols-outlined text-[20px]">{icon}</span>
              </button>
            ))}
            <NotificationPanel
  notifications={notifications}
  unreadCount={unreadCount}
  connected={connected}
  onMarkRead={markRead}
  onMarkAllRead={markAllRead}
  onClearAll={clearAll}
/>

            {/* Divider */}
            <div className="h-6 w-px bg-slate-200 mx-2" />

            {/* ── Profile ── */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setProfileOpen((prev) => !prev)}
                className="flex items-center gap-2.5 px-2.5 py-1.5 rounded-xl hover:bg-slate-50 border border-transparent hover:border-slate-200 transition-all duration-150 focus:outline-none"
                aria-haspopup="true"
                aria-expanded={profileOpen}
              >
                {/* Avatar with online dot */}
                <div className="relative flex-shrink-0">
                  <div className="w-8 h-8 rounded-lg overflow-hidden ring-2 ring-[#08757c]/20 flex items-center justify-center bg-[#08757c]">
                    {user?.avatar ? (
                      <img
                        alt="User avatar"
                        className="w-full h-full object-cover"
                        src={user.avatar}
                      />
                    ) : (
                      <span className="text-white text-sm font-bold leading-none">
                        {(currentEmployeeName || "?").charAt(0).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-400 border-2 border-white rounded-full" />
                </div>

                {/* Name + role */}
                <div className="hidden lg:block text-left max-w-[110px]">
                  <p
                    className="text-[13px] font-bold text-slate-800 leading-tight truncate"
                    title={currentEmployeeName}
                  >
                    {currentEmployeeName}
                  </p>
                  <p
                    className="text-[10px] text-slate-400 font-medium leading-tight mt-0.5 truncate"
                    title={currentRoleName}
                  >
                    {currentRoleName}
                  </p>
                </div>

                <span
                  className="material-symbols-outlined text-[16px] text-slate-400 hidden lg:block transition-transform duration-200"
                  style={{ transform: profileOpen ? "rotate(180deg)" : "rotate(0deg)" }}
                >
                  expand_more
                </span>
              </button>

              {/* ── Dropdown ── */}
              {profileOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-white rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.12)] border border-slate-100 overflow-hidden z-50">

                  {/* User card header */}
                  <div className="px-4 py-4 bg-gradient-to-br from-[#005b60] to-[#08757c]">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl overflow-hidden ring-2 ring-white/30 flex-shrink-0 flex items-center justify-center bg-white/15">
                        {user?.avatar ? (
                          <img
                            alt="avatar"
                            className="w-full h-full object-cover"
                            src={user.avatar}
                          />
                        ) : (
                          <span className="text-white text-base font-bold leading-none">
                            {(currentEmployeeName || "?").charAt(0).toUpperCase()}
                          </span>
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-bold text-white truncate" title={currentEmployeeName}>{currentEmployeeName}</p>
                        <span
                          className="inline-block max-w-full truncate align-bottom mt-0.5 text-[9px] font-bold px-2 py-0.5 rounded-full bg-white/15 text-white/80 uppercase tracking-wide"
                          title={currentRoleName || "User"}
                        >
                          {currentRoleName || "User"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Menu items — hidden for embedded-from-Smile sessions */}
                  {localStorage.getItem("isFromSmile") !== "1" && (
                    <>
                      <div className="py-2">
                        <button
                          onClick={handleChangePassword}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                        >
                          <span className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                            <span className="material-symbols-outlined text-[15px] text-slate-500">lock_reset</span>
                          </span>
                          <span className="font-medium">Change Password</span>
                        </button>
                      </div>

                      <div className="border-t border-slate-100 py-2">
                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                        >
                          <span className="w-7 h-7 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0">
                            <span className="material-symbols-outlined text-[15px] text-red-500">logout</span>
                          </span>
                          <span className="font-semibold">Sign Out</span>
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <ChangePasswordModal
        isOpen={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
      />
      <HolidayCalendarModal
        isOpen={calendarOpen}
        onClose={() => setCalendarOpen(false)}
      />
    </>
  );
};

export default React.memo(TopNav);
